/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import type { DatePickerProps as AntDatePickerProps } from 'antd'
import { DatePicker as AntDatePicker } from 'antd'
import { CalendarOutlined } from '@ant-design/icons'
import Text from '../Text'
import { paragraphMedium, weightRegular } from 'src/components/css'
import dayjs from 'dayjs'

type DatePickerProps = {
  label: string
  value: AntDatePickerProps['value']
  onChange: AntDatePickerProps['onChange']
  minDate?: AntDatePickerProps['value']
  /** Tab index for keyboard navigation */
  tabIndex?: number
  /** Whether the date picker is disabled */
  disabled?: boolean
  /**
   * Also pick a time of day. Defaults to date-only.
   *
   * Enabling this also makes the input typable: date-only pickers stay
   * read-only (calendar selection), but typing a time beats scrolling antd's
   * hour/minute panels.
   */
  showTime?: AntDatePickerProps['showTime']
  /** Display/parse format for the input. Defaults to antd's own. */
  format?: AntDatePickerProps['format']
  /** Placeholder text for the input. */
  placeholder?: string
  /** Renders the error border, matching TextInput's error state. */
  hasError?: boolean
}

/**
 * Converts a date value to a dayjs object if it is a string.
 *
 * @param value - The date value to parse. Can be a string, dayjs object, or undefined.
 * @returns The value as a dayjs object if input is a string, otherwise returns the original value.
 */
const parseDateValueToDayjsIfNeeded = (value: any) => {
  if (typeof value === 'string') {
    return dayjs(value)
  }
  return value
}

const DatePicker = ({
  label,
  value,
  onChange,
  minDate,
  tabIndex,
  disabled,
  showTime = false,
  format,
  placeholder,
  hasError = false,
}: DatePickerProps) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        flex: 1 0 0;
        @media (max-width: 768px) {
          width: 100%;
        }
      `}
    >
      <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
        {label}
      </Text>
      <AntDatePicker
        value={parseDateValueToDayjsIfNeeded(value)}
        onChange={onChange}
        disabledDate={
          minDate
            ? (current) => current && current.isBefore(minDate, 'day')
            : undefined
        }
        showTime={showTime}
        format={format}
        placeholder={placeholder}
        inputReadOnly={!showTime}
        tabIndex={tabIndex}
        disabled={disabled}
        css={css`
          height: 40px !important;
          ${paragraphMedium}
          ${weightRegular}
          width: 100%;
          border-radius: 8px;
          border: 1px solid ${colors.SECONDARY_100};
          ${hasError &&
          `border: 1px solid ${colors.ERROR_300} !important;
             box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;`}
          background: ${colors.SECONDARY_200};
        `}
        suffixIcon={
          <CalendarOutlined
            css={css`
              color: ${colors.WHITE};
              font-size: 20px;
            `}
          />
        }
        getPopupContainer={(triggerNode) => {
          return triggerNode.parentNode as HTMLElement
        }}
        popupStyle={{ position: 'fixed' }}
      />
    </div>
  )
}

export default DatePicker
